<template>
  <v-container id="deviceedit">
    <v-card max-width="800px">
      <v-card-title>{{ title }}</v-card-title>
      <v-card-text>
        <v-form
          ref="form"
        >
        <v-text-field
            v-model="model.name"
            :label="$t('deviceEdit.name')"
          />
          <v-text-field
            v-model="model.deviceID"
            :label="$t('deviceEdit.hardware-unique-id')"
            readonly
            disabled
          />

        <v-select v-model="model.config.operationMode" :label="$t('deviceEdit.operationMode')" :items="operationModes" @change="validate" />

        <!--
        <v-switch v-model="model.config.kiosk" inset @change="validate">
          <template v-slot:label>
            {{ $t('deviceEdit.kiosk-mode') }} <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-on="on" v-bind="attrs" class="ml-2">
                  mdi-help-circle-outline
                </v-icon>
              </template>
              <span v-html="$t('deviceEdit.kiosk-mode-instructions')">I</span>
            </v-tooltip>
          </template>
        </v-switch>
        -->

        <p v-if="model.config.operationMode==='kiosk' && !anonymousAllowed">
{{ $t('deviceEdit.lock-to-user') }}
</p>
        <p v-if="model.config.operationMode==='kiosk' && anonymousAllowed">
{{ $t('deviceEdit.lock-to-user-optional') }}
</p>
        <v-select
          v-if="model.config.operationMode==='kiosk'"
          v-model="model.config.session.content"
          :items="contentLocalised"
          item-text="title"
          item-value="uid"
          :label="$t('deviceEdit.content')"
          @change="validate"
        />
        <v-select
          v-if="model.config.operationMode==='kiosk'"
          v-model="kioskUser"
          :label="$t('deviceEdit.learner')"
          :items="users"
          @change="validate"
        >
          <template v-slot:item="{ item }">
{{ item.displayName }}
</template>
          <template v-slot:selection="{ item }">
{{ item.displayName }}
</template>
        </v-select>
        <v-switch
          v-if="model.config.operationMode==='normal'"
          v-model="model.config.unlockCheckpoints"
          :label="$t('deviceEdit.unlockCheckpoints')"
        />
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          color="blue darken-1"
          @click="cancel"
        >
          {{ $t('global.cancel') }}
</v-btn>
        <v-btn
          :disabled="!valid"
          color="success"
          @click="submit"
        >
          {{ $t('global.save') }}
</v-btn>
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script>
  import api from '@/helpers/api.js'
  import { Users } from '@/helpers/users.js'
  import { ContentLibrary } from '@/helpers/contentLibrary.js'

  export default {
    name: 'DeviceEdit',

    props: {
      device: {
        type: Object,
        required: true,
        default: undefined,
      },
    },

    data () {
      return {
        valid: true,
        loading: true,
        displayName: '',
        users: [],
        content: [],
        assignUser: false,
        kioskUser: undefined,
        model: {
          config: {
            kiosk: false,
            session: {},
            operationMode: 'normal',
          },
        },
        operationModes: ['normal', 'kiosk', 'demo'],
      }
    },

    computed: {
      title: function () {
        return this.model.name
      },
      contentLocalised () {
        const i = this.content.map((x) => {
          return { uid: x.uid, title: x.locales[this.$i18n.locale]?.title ?? x.locales.en?.title ?? x.uid } 
        })
        return i
      },
      anonymousAllowed: function () {
        // eslint-disable-next-line eqeqeq
        return this.$store.getters.currentUserProfile.account.maxUsers == 0
      },
    },

    async mounted () {
      await this.setup()
    },

    methods: {
      getPermittedContent (unfilteredContent) {
        const permittedContent = []
        const permittedPatterns = this.$store.getters.currentUserProfile.account.content
        unfilteredContent.forEach(element => {
          if (permittedPatterns.includes('*') || permittedPatterns.includes(element.uid)) {
            permittedContent.push(element)
          }
        })
        return permittedContent
      },
      validate () {
        if (this.model.config.operationMode === 'kiosk')
        {
          if (!this.model.config.session || !this.model.config.session.content) {
            this.valid = false
            return
          }
          if (!this.anonymousAllowed && !this.kioskUser) {
            this.valid = false
            return
          }
        }

        this.valid = true
      },
      async submit () {
        if (this.model.config.operationMode === 'kiosk')
        {
          this.model.config.kiosk = true
          const assignerLearner = (this.kioskUser && this.kioskUser.id !== '') ? this.kioskUser : undefined
          this.model.config.session.user = assignerLearner ? this.kioskUser.id : undefined
          this.model.config.session.userName = assignerLearner ? this.kioskUser.displayName : undefined
        }
        else
        {
          this.model.config.kiosk = false
          this.model.config.session.user = undefined
          this.model.config.session.userName = undefined
          this.model.config.session.content = undefined
        }
        await api.updateDevice(this.model.id, this.model)
        this.$emit('save')
      },
      cancel () {
        this.$emit('cancel')
      },
      async setup () {
        this.model = JSON.parse(JSON.stringify(this.device))
        if (!this.model.config.session) {
          this.model.config.session = {}
        }

        const contents = await ContentLibrary.Get(this.$store, this.$i18n.locale)
        this.content = contents === null ? [] : contents.filter((x) => { 
          return x.purchased && [ContentLibrary.ContentType.MODULE, ContentLibrary.ContentType.SIMULATOR].includes(x.type)
        })

        const allLearners = await Users.Get(['learner'])
        if (this.anonymousAllowed) {
          this.users = [
            {
              id: '',
              displayName: '-- ANONYMOUS --',
            },
          ].concat(allLearners)
        } else {
          this.users = allLearners
        }
        if (this.model.config.session.user) {
          this.assignUser = true
          this.kioskUser = this.users.filter(x => x.id === this.model.config.session.user)[0]
        } else {
          this.assignUser = false
          this.kioskUser = undefined
        }
        this.validate()
      },
    },
  }
</script>

<style scoped>
  .status {
    padding: 5px 10px;
    border-radius: 5px;
    width: 150px;
    text-align: center;
  }
  .hint {
    font-size: 0.8em;
  }
</style>
